import { useNavigate, useParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createMemo, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { HoldButton } from "@/designSystem/components/hold-button"
import { Text } from "@/designSystem/components/text"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createSubscriptionsRouter } from "@/routes/subscriptions-router"
import {
  sectionsContainerClass,
  tierButtonsClass,
} from "./subscription-item-manage.css"

const SubscriptionItemQuery = graphql(`
  query SubscriptionItemSubscription($subscriptionId: String!) {
    sub(id: $subscriptionId) {
      id
      stripeSubscriptionId

      tier {
        id
        name
      }
      publication {
        id
        name
        logoUrl
        author {
          id
          authorName
          profileImage
        }
      }
    }
  }
`)

const CreateCustomerPortalSessionMutation = graphql(
  `
    mutation CreateCustomerPortalSession($id: ID!) {
      createCustomerPortalSession(id: $id) {
        ... on MutationCreateCustomerPortalSessionSuccess {
          data {
            url
          }
        }
        ...ErrorFragment
      }
    }
  `,
  [ErrorFragment],
)

export function SubscriptionItemManage() {
  const { LL } = useI18nContext()

  const params = useParams()
  const navigate = useNavigate()
  const { paths } = createSubscriptionsRouter()
  const getSubId = createMemo(() => params.subscriptionId!)

  const [subscriptionResult] = createQuery({
    query: SubscriptionItemQuery,
    variables: createMemo(() => ({
      subscriptionId: getSubId(),
    })),
    pause: () => !getSubId(),
    requestPolicy: "cache-and-network",
  })
  const getSub = createMemo(() => subscriptionResult.data?.sub)

  const subscriptionName = createMemo(() => {
    const sub = getSub()
    return sub?.publication?.name ?? ""
  })

  const UnsubscribeMutation = graphql(
    `
    mutation Unsubscribe($targetUserId: ID!) {
      unsubscribeFrom(targetUserId: $targetUserId) {
        ... on MutationUnsubscribeFromSuccess {
          data {
            id
          }
        }
        ...ErrorFragment
      }
    }
  `,
    [ErrorFragment],
  )

  const [unsubscribeState, unsubscribe] = createMutationWithErrorHandling(
    UnsubscribeMutation,
    "unsubscribeFrom",
  )

  const [_portalState, createPortalSession] = createMutationWithErrorHandling(
    CreateCustomerPortalSessionMutation,
    "createCustomerPortalSession",
  )

  async function handleManageBilling() {
    const subscription = getSub()
    if (!subscription) return

    const portalWindow = window.open("", "_blank")

    const result = await createPortalSession(
      { id: subscription.id },
      {
        onSuccess: data => {
          if (data.url) {
            portalWindow!.location.href = data.url
          }
        },
        onError: () => {
          portalWindow?.close()
        },
      },
    )

    if (!result.success) {
      portalWindow?.close()
    }
  }

  async function handleUnsubscribe() {
    const targetUserId = getSub()?.publication?.author?.id
    if (!targetUserId) return

    const result = await unsubscribe({ targetUserId })

    if (result.success) {
      toast.success(LL().settings.subscriptions.unsubscribed())
      navigate(paths().manage().url)
    }
  }

  return (
    <MainLayout
      hue="indigo"
      header={
        <Navigation
          hue="indigo"
          buttons={[
            {
              label: LL().subscriptions.title(),
              href: paths().url,
            },
            {
              label: LL().subscriptions.manage(),
              href: paths().manage().url,
            },
          ]}
          title={subscriptionName()}
        />
      }
      query={subscriptionResult}
    >
      {data => {
        const sub = createMemo(() => data?.sub)

        return (
          <Show when={sub()}>
            {s => (
              <div class={sectionsContainerClass}>
                <Show when={s().stripeSubscriptionId}>
                  <Section
                    title={LL().subscriptions.manageTitle()}
                    description=""
                    hue="indigo"
                    iconName="wallet"
                  >
                    <Well>
                      <div class={tierButtonsClass}>
                        <Button hue="sand" onClick={handleManageBilling}>
                          {LL().subscriptions.manageBilling()}
                        </Button>
                      </div>
                    </Well>
                  </Section>
                </Show>

                <Section
                  title={LL().settings.subscriptions.unsubscribeTitle()}
                  description={LL().settings.subscriptions.unsubscribeDescription()}
                  hue="rose"
                  iconName="trash-2"
                >
                  <Well hue="rose">
                    <Show when={s().tier}>
                      <Text fontSize="s" color="rose700" textAlign="center">
                        {LL().settings.subscriptions.unsubscribePaidDisclaimer()}
                      </Text>
                    </Show>
                    <Box alignItems="center" justifyContent="center">
                      <HoldButton
                        hue="rose"
                        label={LL().settings.subscriptions.holdToUnsubscribe()}
                        labelProgress={LL().settings.subscriptions.unsubscribing()}
                        labelCompleted={LL().settings.subscriptions.unsubscribed()}
                        icon="message-square-x"
                        disabled={unsubscribeState.fetching}
                        onHoldComplete={handleUnsubscribe}
                      />
                    </Box>
                  </Well>
                </Section>
              </div>
            )}
          </Show>
        )
      }}
    </MainLayout>
  )
}
